import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Spinner } from "reactstrap";
import { IoPrintOutline, IoBanOutline, IoTrashOutline } from "react-icons/io5";
import { toast } from "react-toastify";
import SidebarLayout from "../../../../layouts/SidebarLayout";
import sidebar from "../../utils/sidebar";
import PageSubHeader from "../../../../components/Layouts/PageSubHeader";
import DefaultButton from "../../../../components/Buttons/Default";
import ConfirmationModal from "../../../../components/Modals/Confirmation";
import { del, get, post } from "../../../../utils/helpers/api";
import DocumentSheet from "./Print/DocumentSheet";

function ViewDocumentPage() {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();

  const [document, setDocument] = useState(null);
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState(false);
  const [actionToConfirm, setActionToConfirm] = useState(null);

  const getDocument = async () => {
    const data = await get(`/hr/documents/view/${id}`);
    if (data?.success) {
      setDocument(data.document);
    } else {
      toast.error(data?.message || t('common.somethingWentWrong'));
    }
    setLoading(false);
  };

  useEffect(() => {
    getDocument();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const cancelDocument = async () => {
    setWorking(true);
    const data = await post(`/hr/documents/cancel/${id}`, {});
    if (data?.success) {
      await getDocument();
      toast.success(t('hr.documents.cancelled'));
    } else {
      toast.error(data?.message || t('common.somethingWentWrong'));
    }
    setWorking(false);
  };

  const deleteDocument = async () => {
    setWorking(true);
    const data = await del(`/hr/documents/delete/${id}`);
    if (data?.success) {
      toast.success(t('alerts.success_delete'));
      navigate('/hr/documents');
    } else {
      toast.error(data?.message || t('common.somethingWentWrong'));
      setWorking(false);
    }
  };

  const CONFIRMATIONS = {
    cancel: {
      title: t('hr.documents.cancelTitle'),
      message: t('hr.documents.cancelMessage'),
      button: t('hr.documents.cancelDocument'),
      run: cancelDocument,
    },
    delete: {
      title: t('hr.documents.deleteTitle'),
      message: t('hr.documents.deleteMessage'),
      button: t('common.delete'),
      run: deleteDocument,
    },
  };

  if (loading) {
    return (
      <SidebarLayout sidebar={sidebar}>
        <Spinner color="primary" className="d-block mx-auto my-5" />
      </SidebarLayout>
    );
  }

  return (
    <SidebarLayout sidebar={sidebar}>
      <div className="page-with-subheader">
        <div className="no-print">
          <PageSubHeader
            breadcrumb={[t('sidebar.documents'), document?.reference]}
            onBack={() => navigate('/hr/documents')}
          >
            <DefaultButton color="primary" size="sm" onClick={() => window.print()}>
              <IoPrintOutline className="me-1" />
              {t('hr.documents.print')}
            </DefaultButton>
            {document?.status === 'issued' && (
              <DefaultButton
                color="danger"
                size="sm"
                outline
                disabled={working}
                onClick={() => setActionToConfirm('cancel')}
              >
                <IoBanOutline className="me-1" />
                {t('hr.documents.cancelDocument')}
              </DefaultButton>
            )}
            <DefaultButton
              color="danger"
              size="sm"
              outline
              disabled={working}
              onClick={() => setActionToConfirm('delete')}
            >
              <IoTrashOutline />
            </DefaultButton>
          </PageSubHeader>
        </div>

        <div className="subheader-page-content scrollable" style={{ background: "#f4f5f7", padding: "20px 0" }}>
          <div className="doc-print-root">
            <DocumentSheet document={document} />
          </div>
        </div>
      </div>

      <ConfirmationModal
        isOpen={actionToConfirm !== null}
        title={actionToConfirm ? CONFIRMATIONS[actionToConfirm].title : ''}
        message={actionToConfirm ? CONFIRMATIONS[actionToConfirm].message : ''}
        confirmationText={actionToConfirm ? CONFIRMATIONS[actionToConfirm].button : ''}
        onConfirm={() => CONFIRMATIONS[actionToConfirm].run()}
        toggle={() => setActionToConfirm(null)}
      />
    </SidebarLayout>
  );
}

export default ViewDocumentPage;
